import {
  getMonthDates,
  getMonthNames,
  getNumbers,
  useTokenValues
} from '@jds/react-engine';

import { useMemo } from 'react';

export const getNormalisedDate = (value?: string | Date) => {
  const date = new Date(value || new Date());

  if (!date.getTime()) {
    return { day: 0, month: 0, year: 0 };
  }
  return {
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear()
  };
};

export function removeTime(d: Date): Date {
  d.setHours(0, 0, 0, 0);
  return d;
}

const isToday = (date: Date, month: number, year: number, day: number) => {
  if (year !== date.getFullYear()) return false;
  if (month !== date.getMonth()) return false;
  if (day !== date.getDate()) return false;
  return true;
};

const isDateDisabled = (
  value: string | Date,
  disabled: Array<string | Date>,
  dateFilter?: (value: string | Date) => boolean,
  min?: string,
  max?: string
) => {
  if (dateFilter) {
    return dateFilter(value);
  }
  const { day, month, year } = getNormalisedDate(value);

  const disabledData = disabled.map((d) => getNormalisedDate(d));
  const disabledIndex = disabledData.findIndex((d) => {
    if (!(d.day === day)) return false;
    if (!(d.month === month)) return false;
    if (!(d.year === year)) return false;
    return true;
  });

  if (disabledIndex >= 0) return true;

  if (min && max) {
    const minDate = removeTime(new Date(min));
    const maxDate = removeTime(new Date(max));
    const currentValue = removeTime(new Date(value));
    return !(currentValue <= maxDate && currentValue >= minDate);
  }

  if (min) {
    const minDate = removeTime(new Date(min));
    const currentValue = removeTime(new Date(value));
    return !(currentValue >= minDate);
  }

  if (max) {
    const maxDate = removeTime(new Date(max));
    const currentValue = removeTime(new Date(value));
    return !(currentValue <= maxDate);
  }
};

const getDayState = (
  day: number,
  month: number,
  year: number,
  currentCount: number,
  selected?: Date
) => {
  const today = new Date();
  const selectedDate = getNormalisedDate(selected);

  if (
    day - 1 === currentCount &&
    selectedDate.month === month &&
    selectedDate.year === year
  )
    return 'selected';

  if (isToday(today, month - 1, year, currentCount + 1)) {
    return 'current';
  }

  return 'default';
};

export const createRows = (
  month = 0,
  year = 0,
  day = 0,
  disabled: (string | Date)[] = [],
  dateFilter?: (value: string | Date) => boolean,
  selected?: Date,
  min?: string,
  max?: string
) => {
  const tokens = useTokenValues();
  const language = tokens.lang;

  const getMonthDatesFromLocale = useMemo(() => {
    return getMonthDates(language);
  }, [language]);
  const visibleMonth = removeTime(new Date(year, month - 1, 1));
  const firstDayOfMonth = visibleMonth.getDay();

  const daysInMonth = new Date(year, month, 0).getDate();

  const blanks: {
    state?: string;
    value?: number;
    disabled?: boolean;
  }[] = [];

  for (let i = 0; i < firstDayOfMonth; i++) {
    blanks.push({ state: 'empty' });
  }
  const finalRows: { row: { value?: number; state?: string }[] }[] = [];

  const rows = Array.from({ length: daysInMonth }).map<{
    value?: number;
    state?: string;
    disabled?: boolean;
  }>((_, i) => {
    return {
      value: i + 1,
      internalIndex: i + 1,
      state: getDayState(day, month, year, i, selected),
      disabled: isDateDisabled(
        new Date(year, month - 1, i + 1),
        disabled,
        dateFilter,
        min,
        max
      )
    };
  });

  const rowsWithBlanks = blanks.concat(rows);

  while (rowsWithBlanks.length % 7 !== 0) {
    rowsWithBlanks.push({
      state: 'empty'
    });
  }

  let sum = 0;
  while (sum <= daysInMonth + 7) {
    const localizedRows = rowsWithBlanks
      .slice(sum, sum + 7)
      .map(({ value, state, disabled, ...rest }) => ({
        value: value ? getMonthDatesFromLocale[value - 1] : undefined, // Adjust index
        state,
        disabled,
        ...rest
      }));

    finalRows.push({ row: localizedRows });
    sum = sum + 7;
  }

  return finalRows;
};

export const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const loadedYears = (
  max = 0,
  min = 0,
  selected: number,
  disabled?: boolean[]
) => {
  const tokens = useTokenValues();
  const language = tokens.lang;
  const numeralList = useMemo(() => {
    return getNumbers(language);
  }, [language]);
  const getLocaleYear = (year: number) => {
    const yearString = year?.toString();
    return yearString?.replace(
      /\d/g,
      (num) => numeralList[parseInt(num, 10)] ?? num
    );
  };

  return Array.from({ length: max - min }).map((_, i) => {
    return {
      name: getLocaleYear(min + i),
      value: min + i,
      selected: min + i === selected,
      disabled: disabled?.[i] === true
    };
  });
};

export const loadedMonths = (
  selected: number,
  selectedYear: number,
  selectable?: boolean,
  min?: string | Date,
  max?: string | Date
) => {
  const { month: minMonth, year: minYear } = getNormalisedDate(min);
  const { month: maxMonth, year: maxYear } = getNormalisedDate(max);

  const tokens = useTokenValues();
  const language = tokens?.lang;
  const getMonthNamesFromLocale = useMemo(() => {
    return getMonthNames(language);
  }, [language]);
  const yearValid =
    (selectedYear >= minYear || !min) && (selectedYear <= maxYear || !max);

  return getMonthNamesFromLocale.map((month, i) => {
    let disabled = false;
    if (yearValid) {
      if (min) {
        disabled = i + 1 < minMonth;
      }
      if (max) {
        disabled = disabled || i + 1 > maxMonth;
      }
    }

    return {
      name: month,
      value: i,
      selected: selected === i,
      disabled: !selectable || disabled
    };
  });
};

export const getOffset = (ref: HTMLElement) => {
  if (!ref) return { x: 0, y: 0 };
  const { x, y, height } = ref.getBoundingClientRect();

  return { x, y: y + height };
};

export const getDisabledYears = (
  minYear: number,
  maxYear: number,
  minDate?: string | Date,
  maxDate?: string | Date
) => {
  if (!minDate && !maxDate) return [];
  const { year: currentMinYear } = getNormalisedDate(minDate);
  const { year: currentMaxYear } = getNormalisedDate(maxDate);
  const years: boolean[] = Array.from({ length: maxYear - minYear }).map(
    (_, i) => {
      const year = minYear + i;
      if (minDate && maxDate)
        return year < currentMinYear || year > currentMaxYear;
      if (minDate) return year < currentMinYear;
      if (maxDate) return year > currentMaxYear;
      return false;
    }
  );
  return years;
};
